<!--
    Component Artist Detail
    A detailed view of an Artist
    @author Leander Eyer <leander.eyer@elead.ch>
-->

<template>

	<div class='container'>
		<div id='artist-detail-content' class='artist-detail-content content' :class="{ hidden: cursor >= 0 }">
			
			<div class="bar darkText largeText">
				<div class="gridLabel bg-artist">{{ model.getDictionary('nav_artist') }}</div>

				<div class="gridTriple bg-artist-light">
					{{ artist.getFullName() }}
					<div class="link artist-detail-close" @click="$parent.select()">x</div>
				</div>
			</div>		

			<div class="artist-detail-grid" @wheel.prevent="scroll($event.deltaY > 0? 1 : -1)">
				<div class="artist-detail-image zoomable link"
					v-for="(work, index) in list.slice(slice, slice + sliceSize)"
					:key="work.getID()"
				>
					<img
						v-if="work.getImages().isImage()"
						:src="work.getImages().getImageURL(previewImageQuality)"
						:title="work.getToolTip()"
						@click="select(index + slice)"
					/>
					<div v-else 
						v-html="work.getImages().getMultiMediaHTML(0, 'preview')" 
						:title="work.getToolTip()"
						@click="select(index + slice)"></div>
				</div>
			</div>

			<div
				class="artist-detail-grid-left arrow"
				:class="{ invisible: slice <= 0 }"
				@click.left="scroll(-1)"
				@mousedown.left="autoscroll('start', -1)"
				@mouseup.left="autoscroll('stop')"
			>&#x25C0;</div>
			<div
				class="artist-detail-grid-right arrow"
				:class="{ invisible: slice >= maxSlice }"
				@click.left="scroll(1)"
				@mousedown.left="autoscroll('start', 1)"
				@mouseup.left="autoscroll('stop')"
			>&#x25B6;</div>


			<div v-if="artist.getDescription() != ''" class='artist-detail-description'>
				{{ artist.getDescription()  }}
			</div>

			<!-- Detail Information -->
			<table class="artist-detail-data">

				<tr v-if=" artist.getBirthYear() != ''">
					<td>{{ model.getDictionary('artist_birth') }}:</td>
					<td>{{ artist.getBirthYear() }} {{ model.getDictionary('artist_in') }} {{ artist.getBirthLocation() }}</td>
				</tr>

				<tr v-if=" artist.getDeathYear() != ''">
					<td>{{ model.getDictionary('artist_death') }}:</td>
					<td>{{ artist.getDeathYear() }} {{ model.getDictionary('artist_in') }} {{ artist.getDeathLocation() }}</td>
				</tr>

				<tr v-if=" artist.getLink() != ''">
					<td>{{ model.getDictionary('artist_website') }}:</td>
					<td><a class='artist-detail-link' :href="artist.getLink()" target='_BLANK'>{{ artist.getLink() }}</a></td>
				</tr>				
			</table>

		</div>
		<work-detail v-if="cursor >= 0" :list="list" :index="cursor"></work-detail>
	</div>
</template>

<script>

	import Vue from "vue";
	import { model } from "./../model/Model.js";
	import { GRID_COLUMNS } from "./../model/config.js";
	import { ListWithCursor } from "./../mixin/ListWithCursor.js";
	import { Scrollable } from "./../mixin/Scrollable.js";
	import WorkDetail from "./WorkDetail.vue";

	export default {

		mixins: [ListWithCursor, Scrollable],
		components: { WorkDetail },
		props: ["artist"],

		data: function() {
			return {
				// expose model for translations
				model: model,
				// source of the preview images
				previewImageQuality: 'small',
				// number of cells visible at once
				sliceSize: 6,
				// scroll velocity
				scrollBy: 1
			};
		},

		// Initialize the component
		created: function() {
			this.list = this.artist.getWorks();
			this.sliceSize = Vue.prototype.$device == 'large'? GRID_COLUMNS : 2;
			this.maxSlice = Math.max(0, this.list.length - this.sliceSize);
			this.previewImageQuality = Vue.prototype.$previewImageQuality;
		}		

	};
</script>

<style>

@media (min-width: 1000px) {

	div.artist-detail-close {
		display: inline;
		position: absolute;
		right: 5rem;
		top: 0rem;
	}

	div.artist-detail-grid {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;
	}


	div.artist-detail-grid-left {
		position: absolute;
		top: 8.5rem;
		left: -3rem;
		cursor: pointer;
		user-select: none;
	}

	div.artist-detail-grid-right {
		position: absolute;
		top: 8.5rem;
		right: -1rem;
		cursor: pointer;
		user-select: none;
	}		

	div.artist-detail-image {
		width: 9rem;
		height: 9rem;
		margin: 0 2rem 2rem 0;
		overflow: hidden;
	}

	div.artist-detail-image img, div.artist-detail-image div {
		object-fit: cover;
		object-position: center;
		width: 9rem;
		height: 9rem;
	}	

	div.artist-detail-description {
		margin: 2rem 4rem 0 0;
	}

	table.artist-detail-data {
		margin-top: 2rem;
	}

	table.artist-detail-data td {
		padding: 0.25rem 3rem 0.25rem 0;
	}

	tr.artist-detail-title {
		font-weight: bold;
	}

}

@media (max-width: 1000px) {

	div.artist-detail-close {
		display: inline;
		position: absolute;
		right: 1rem;
		top: 2.7rem;
	}

	div.artist-detail-grid {
		overflow: hidden;

		flex-grow: 1;
		flex-basis: 0;

		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: stretch;
		align-content: stretch;

		height: 18rem;

		margin: 0 -2rem -2rem 0;
	}


	div.artist-detail-grid-left {
		display: inline-block;
		font-size: 2rem;
		padding: 1rem 2rem 1rem 0rem;
		cursor: pointer;
		user-select: none;
	}

	div.artist-detail-grid-right {
		display: inline-block;
		font-size: 2rem;
		padding: 1rem 2rem 1rem 2rem;
		cursor: pointer;
		user-select: none;
	}		

	div.artist-detail-image {
		flex-grow: 1;
		width: calc(50% - 2rem);
		max-width: calc(50% - 2rem);
		height: 16rem;
		margin: 0 2rem 2rem 0;
		overflow: hidden;
	}

	div.artist-detail-image img, div.artist-detail-image div {
		object-fit: cover;
		object-position: center;
		width: 100%;
		height: 100%;
	}

	div.artist-detail-description {
		margin: 2rem 4rem 0 0;
	}

	table.artist-detail-data {
		margin-top: 2rem;
	}

	table.artist-detail-data td {
		padding: 0.25rem 3rem 0.25rem 0;
	}

	a.artist-detail-link {
		white-space: nowrap;
	}

	tr.artist-detail-title {
		font-weight: bold;
	}

}


</style>